/**
 * Building blocks every analytics screen shares: a titled panel, and the
 * loading, empty and error states that stand in for one. A panel is never left
 * blank — it either shows data, says why there is none, or says what failed.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, BarChart3, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

export function AnalyticsPanel({
  title,
  description,
  action,
  children,
  className = '',
  contentClassName = '',
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Card className={`rounded-xl border-border bg-card hover:shadow-card ${className}`}>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 p-4 pb-2 sm:p-5 sm:pb-2">
        <div className="min-w-0">
          <CardTitle className="text-base font-semibold text-foreground">{title}</CardTitle>
          {description && <CardDescription className="mt-1 text-xs">{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent className={`p-4 pt-2 sm:p-5 sm:pt-2 ${contentClassName}`}>{children}</CardContent>
    </Card>
  );
}

export function AnalyticsEmptyState({
  title = 'No performance data yet',
  description = 'Performance analytics will appear after students complete a lesson.',
  compact = false,
}: {
  title?: string;
  description?: string;
  compact?: boolean;
}) {
  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-6' : 'py-12'}`}>
      <BarChart3 className="mb-3 h-8 w-8 text-muted-foreground/50" aria-hidden />
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

export function AnalyticsErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium text-destructive">Couldn’t load performance data</p>
        <p className="mt-0.5 text-muted-foreground">{message}</p>
        {onRetry && (
          <button type="button" onClick={onRetry} className="mt-2 text-xs font-medium text-primary hover:underline">
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

/** A quiet note under a panel — how a figure is calculated, or what it leaves out. */
export function AnalyticsNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
      <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

export function ChartSkeleton({ height = 240 }: { height?: number }) {
  return <Skeleton className="w-full rounded-lg" style={{ height }} />;
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
